import React from 'react';
import { Link } from 'react-router-dom';
import './Training.css';

const courses = [
  {
    code: 'WAST',
    name: 'Web Application Security Testing',
    level: 'Intermediate',
    icon: '/images/training/web-app-security.png',
    desc: 'Master the art of identifying and exploiting web vulnerabilities. Covers OWASP Top 10, SQL injection, XSS, CSRF, and advanced attack chains.',
    topics: ['OWASP Top 10', 'Burp Suite', 'API Security', 'Authentication Attacks'],
    color: 'green',
  },
  {
    code: 'AWS-SEC',
    name: 'AWS Certified Security – Specialty',
    level: 'Advanced',
    icon: '/images/training/aws-security.png',
    desc: 'Validate your expertise in securing the AWS environment. Covers identity management, infrastructure protection, data protection, and incident response.',
    topics: ['IAM', 'KMS', 'CloudTrail', 'Security Hub'],
    color: 'orange',
  },
  {
    code: 'CND',
    name: 'Certified Network Defender',
    level: 'Intermediate',
    icon: '/images/training/network-defender.png',
    desc: 'Develop skills to protect, detect, and respond to network-based threats. Hands-on labs covering firewalls, IDS/IPS, VPNs, and network monitoring.',
    topics: ['Network Defense', 'IDS/IPS', 'Firewall Config', 'Incident Response'],
    color: 'teal',
  },
  {
    code: 'CCOA',
    name: 'Cisco Certified CyberOps Associate',
    level: 'Beginner',
    icon: '/images/training/cisco-cyberops.png',
    desc: 'Gain the skills to work in SOC environments. Covers security monitoring, intrusion analysis, and incident response fundamentals.',
    topics: ['SOC Operations', 'Security Monitoring', 'SIEM', 'Threat Analysis'],
    color: 'green',
  },
  {
    code: 'CCSP',
    name: 'Certified Cloud Security Professional',
    level: 'Advanced',
    icon: '/images/training/cloud-security-pro.png',
    desc: 'Become a cloud security expert. Covers cloud architecture, data security, platform and infrastructure security, and legal/compliance requirements.',
    topics: ['Cloud Architecture', 'Data Security', 'Compliance', 'Risk Management'],
    color: 'orange',
  },
  {
    code: 'CFR',
    name: 'CyberSec First Responder',
    level: 'Intermediate',
    icon: '/images/training/cybersec-first-responder.png',
    desc: 'Prepare to identify, contain, eradicate, and recover from cybersecurity incidents. Covers incident handling, threat intelligence, and forensics basics.',
    topics: ['Incident Handling', 'Threat Intel', 'Digital Forensics', 'Malware Analysis'],
    color: 'teal',
  },
  {
    code: 'CEH',
    name: 'Certified Ethical Hacker',
    level: 'Intermediate',
    icon: '/images/training/ethical-hacker.png',
    desc: 'Think like a hacker, defend like a pro. Master 20 hacking domains including foot-printing, scanning, enumeration, and session hijacking.',
    topics: ['Penetration Testing', 'Exploitation', 'Social Engineering', 'Cryptography'],
    color: 'green',
  },
  {
    code: 'CISSP',
    name: 'Certified Information Systems Security Professional',
    level: 'Advanced',
    icon: '/images/training/cissp.png',
    desc: 'The gold standard in cybersecurity certification. Covers 8 CISSP domains from security & risk management to software development security.',
    topics: ['8 CISSP Domains', 'Risk Management', 'Access Control', 'Cryptography'],
    color: 'orange',
  },
  {
    code: 'SEC+',
    name: 'CompTIA Security+',
    level: 'Beginner',
    icon: '/images/training/comptia-security-plus.png',
    desc: 'The most widely adopted, vendor-neutral security certification. Perfect entry point for IT professionals moving into cybersecurity.',
    topics: ['Network Security', 'Compliance', 'Threats & Attacks', 'Cryptography'],
    color: 'teal',
  },
];

const levelColor = { Beginner: 'green', Intermediate: 'blue', Advanced: 'red' };

export default function Training() {
  return (
    <div className="training-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="container page-hero-content">
          <span className="section-label section-label--blue">Education & Certification</span>
          <h1>Cybersecurity <span style={{ color: '#d5772d' }}>Training Programs</span></h1>
          <p>Bridge the knowledge gap with hands-on, industry-relevant training. Our expert-led programs prepare professionals at every level - from foundational skills to elite certifications.</p>
        </div>
      </section>

      {/* Value Props */}
      <section className="training-values">
        <div className="container">
          <div className="values-grid">
            {[
              { icon: '/images/training/passionate-educators.png', title: 'Passionate Educators', color: 'green', desc: 'Industry experts committed to sharing deep knowledge and real-world experience from active cybersecurity careers.' },
              { icon: '/images/training/student-centric.png', title: 'Student-Centric Approach', color: 'orange', desc: 'Personalized learning paths that accommodate different skill levels, from beginners to experienced professionals.' },
              { icon: '/images/training/industry-curriculum.png', title: 'Industry-Relevant Curriculum', color: 'teal', desc: 'Programs developed collaboratively with industry leaders, emphasizing real-world application and current threat landscapes.' },
            ].map((v, i) => (
              <div className="value-card" key={i}>
                <div className={`value-icon value-icon--${v.color}`}><img src={v.icon} alt={v.title} /></div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses */}
      <section className="courses-section">
        <div className="container">
          <div className="section-header centered">
            <span className="section-label">Certification Programs</span>
            <h2 className="section-title">9 Industry-Recognized <span>Programs</span></h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>
              From entry-level foundations to advanced specialist certifications - find the right program for your career goals.
            </p>
          </div>
          <div className="courses-grid">
            {courses.map((course, i) => (
              <CourseCard key={i} course={course} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="training-cta">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-glow" />
            <div className="cta-content">
              <h2>Ready to Advance Your Cybersecurity Career?</h2>
              <p>Contact us today to explore how we can tailor our training programs to meet your specific needs and career objectives.</p>
              <div className="cta-actions">
                <Link to="/jobs" className="btn-outline-white">View Open Roles</Link>
                <Link to="/contact" className="btn-primary">
                  Get Started Today
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function CourseCard({ course }) {
  return (
    <div className={`course-card course-card--${course.color}`}>
      <div className="course-header">
        <div className="course-icon"><img src={course.icon} alt={course.name} /></div>
        <div className="course-meta">
          <span className="course-code">{course.code}</span>
          <span className={`course-level level--${levelColor[course.level]}`}>{course.level}</span>
        </div>
      </div>
      <h3 className="course-name">{course.name}</h3>
      <p className="course-desc">{course.desc}</p>
      <div className="course-topics">
        {course.topics.map(t => (
          <span key={t} className="topic-tag">{t}</span>
        ))}
      </div>
      <Link to="/contact" className="course-enroll">
        Enroll Now
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
      </Link>
    </div>
  );
}
